import modalActions from "actions/modal";
import { Transition } from "components/shared/column";
import DropdownBtn from "components/shared/dropdown";
import Loader from "components/shared/loader";
import { dateTimeToView } from "helpers/date";
import {
    relationToTypesEnum,
    universalDocumentSendStatuses,
    universalDocumentStatuses,
    universalDocumentSystems,
    universalTransferDocumentModes,
    universalTransferDocumentTypes,
    universalTransferDocumentTypesEnum,
} from "helpers/enums";
import { paths } from "helpers/paths";
import { connect } from "helpers/redux";
import { enumLabel, formatNumber } from "helpers/system";
import React, { useEffect, useState } from "react";
import { CustomScroll } from "react-custom-scroll";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { createTaxInvoice, delItem, getItem } from "sources/books/universalTransferDocuments";
import EditUniversalTransferDocumentForm from "./editForm";
import RegenerateUniversalTransferDocumentForm from "./regenerateForm";

const hasManualValues = (manualValues) => {
    if (!manualValues) {
        return false;
    }
    if (Array.isArray(manualValues)) {
        return manualValues.length > 0;
    }
    if (typeof manualValues === "object") {
        return Object.keys(manualValues).length > 0;
    }
    return true;
};

const formatManualValues = (manualValues) => {
    if (typeof manualValues === "string") {
        return manualValues;
    }
    return JSON.stringify(manualValues, null, 2);
};

const RelatedObject = ({ document }) => {
    if (!document?.relatedToId) {
        return null;
    }
    switch (document.relatedToType) {
        case relationToTypesEnum.Bid:
            return <Link to={paths.bidShow("bid", document.relatedToId)}>Заявка: {document.relatedToId}</Link>;
        case relationToTypesEnum.DistributionBid:
            return <Link to={paths.distributionBidShow(document.relatedToId)}>Заказ: {document.relatedToId}</Link>;
        default:
            return `${document.relatedToType || ""} ${document.relatedToId || ""}`;
    }
};

const UniversalDocumentInfo = ({ title, document, registryId }) => {
    if (!document) {
        return null;
    }
    const showRelatedObject = !!document.relatedToId && !registryId;

    return (
        <div className="mb-4">
            <h6>{title}</h6>
            <table className="table table-sm">
                <tbody>
                    <tr>
                        <td>ID</td>
                        <td>
                            <Link to={paths.financesDocumentsShow(document.id)}>{document.id}</Link>
                        </td>
                    </tr>
                    <tr>
                        <td>Название</td>
                        <td>{document.name}</td>
                    </tr>
                    <tr>
                        <td>Статус документа</td>
                        <td>{enumLabel(universalDocumentStatuses, document.status)}</td>
                    </tr>
                    <tr>
                        <td>Статус отправки</td>
                        <td>{enumLabel(universalDocumentSendStatuses, document.sendStatus)}</td>
                    </tr>
                    <tr>
                        <td>Система документооборота</td>
                        <td>{enumLabel(universalDocumentSystems, document.system)}</td>
                    </tr>
                    {showRelatedObject && (
                        <tr>
                            <td>Связанный объект</td>
                            <td>
                                <RelatedObject document={document} />
                            </td>
                        </tr>
                    )}
                    <tr>
                        <td>Файлы</td>
                        <td>
                            {(document.files || []).map((item) => (
                                <a key={item.id} className="d-block" href={item.accessUrlLink} download>
                                    {item.originalFileName}
                                </a>
                            ))}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

const UniversalTransferDocumentsShow = (props) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({});
    const { id } = useParams();

    const getData = () => {
        setLoading(true);
        getItem(id)
            .then((res) => setData(res || {}))
            .catch(() => undefined)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        getData();
    }, [id]);

    const openEditForm = () => {
        props.modal({
            name: "default",
            title: "Редактировать УПД",
            windowMod: "sm",
            CustomBody: EditUniversalTransferDocumentForm,
            bodyData: { data, onSuccess: getData },
        });
    };

    const openRegenerateForm = () => {
        props.modal({
            name: "default",
            title: "Перегенерировать файлы УПД",
            windowMod: "sm",
            CustomBody: RegenerateUniversalTransferDocumentForm,
            bodyData: { id, onSuccess: getData },
        });
    };

    const handleCreateTaxInvoice = () => {
        setLoading(true);
        createTaxInvoice({ id }).then(() => getData());
    };

    const handleDelete = (deleteRegistry) => {
        setLoading(true);
        props.modal({
            name: "confirm",
            title: "Удаление УПД",
            text: "Вы действительно хотите удалить УПД?",
            onSuccessAction: () => props.history.push(paths.universalTransferDocuments),
            onCloseAction: () => setLoading(false),
            buttons: { confirm: "Да", cancel: "Нет" },
            action: () => delItem({ id, deleteRegistry }),
        });
    };

    const renderHead = () => (
        <div className="book-list__head">
            <div
                className="book-show__close close close--sm"
                onClick={() => props.history.push(paths.universalTransferDocuments)}
            />
            <div className="book-show__title">УПД {data.number || data.displayNumber || data.id || ""}</div>
        </div>
    );

    const renderActions = () => {
        const removeItems = data.registryId
            ? [
                  { text: "Удалить только УПД", action: () => handleDelete(false) },
                  { text: "Удалить с реестром", action: () => handleDelete(true) },
              ]
            : [{ text: "Удалить", action: () => handleDelete(false) }];
        const canCreateTaxInvoice =
            data.type === universalTransferDocumentTypesEnum.TransferOnly &&
            !data.taxInvoiceUniversalDocument &&
            !data.taxInvoiceUniversalDocumentId;
        return (
            <div className="mb-3">
                <div className="btn btn-xs btn-outline-primary mr-2" onClick={openEditForm}>
                    Редактировать
                </div>
                {canCreateTaxInvoice && (
                    <div className="btn btn-xs btn-outline-primary mr-2" onClick={handleCreateTaxInvoice}>
                        Создать счет-фактуру
                    </div>
                )}
                <div className="btn btn-xs btn-outline-primary mr-2" onClick={openRegenerateForm}>
                    Перегенерировать файлы
                </div>
                <DropdownBtn cname="btn btn-xs btn-outline-danger" text="Удалить" items={removeItems} />
            </div>
        );
    };

    const renderBody = () => (
        <div className="pt-3">
            {renderActions()}
            <table className="table table-sm">
                <tbody>
                    <tr>
                        <td>ID</td>
                        <td>{data.id}</td>
                    </tr>
                    <tr>
                        <td>Номер</td>
                        <td>{data.number || data.displayNumber}</td>
                    </tr>
                    <tr>
                        <td>Создан</td>
                        <td>{dateTimeToView(data.createdAt)}</td>
                    </tr>
                    <tr>
                        <td>Статус УПД</td>
                        <td>{enumLabel(universalTransferDocumentTypes, data.type)}</td>
                    </tr>
                    <tr>
                        <td>Режим</td>
                        <td>{enumLabel(universalTransferDocumentModes, data.mode)}</td>
                    </tr>
                    <tr>
                        <td>Требует проверки</td>
                        <td>{data.isPendingManualUpdate ? "Да" : "Нет"}</td>
                    </tr>
                    <tr>
                        <td>Контрагент</td>
                        <td>{data.counterparty?.displayName || data.counterparty?.name}</td>
                    </tr>
                    <tr>
                        <td>Реестр</td>
                        <td>
                            {data.registryId && (
                                <Link to={paths.registriesShow(data.registryId)}>{data.registryId}</Link>
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td>Комментарий</td>
                        <td>{data.comment}</td>
                    </tr>
                    <tr>
                        <td>Сумма без НДС</td>
                        <td>{formatNumber(data.totalWithoutVat)}</td>
                    </tr>
                    <tr>
                        <td>НДС</td>
                        <td>
                            {data.totalVat === null || data.totalVat === undefined
                                ? "Без НДС"
                                : formatNumber(data.totalVat)}
                        </td>
                    </tr>
                    <tr>
                        <td>Сумма с НДС</td>
                        <td>{formatNumber(data.total ?? data.totalWithVat)}</td>
                    </tr>
                    {hasManualValues(data.manualValues) && (
                        <tr>
                            <td>Переопределенные значения</td>
                            <td>
                                <pre className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                                    {formatManualValues(data.manualValues)}
                                </pre>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            <UniversalDocumentInfo
                title="Основной документ"
                document={data.primaryUniversalDocument}
                registryId={data.registryId}
            />
            <UniversalDocumentInfo
                title="Счет-фактура"
                document={data.taxInvoiceUniversalDocument}
                registryId={data.registryId}
            />
        </div>
    );

    return (
        <div className="page-content">
            <Transition dir="full" {...props}>
                <div className="page-book__show book-show">
                    {renderHead()}
                    <div className="book-show__body">
                        {loading ? (
                            <Loader prefix="absolute-center" />
                        ) : (
                            <CustomScroll heightRelativeToParent="100%">{renderBody()}</CustomScroll>
                        )}
                    </div>
                </div>
            </Transition>
        </div>
    );
};

function container(Component) {
    const mapDispatch = (dispatch) => ({
        modal: (params) => dispatch(modalActions.open(params)),
    });

    return connect(null, mapDispatch)(Component);
}

export default container(UniversalTransferDocumentsShow);
